import type { MutableRefObject } from 'react';
import React, { useMemo } from 'react';
import { Content, Group, Item, Portal, Root, Trigger } from '@radix-ui/react-dropdown-menu';

import useMartyContext from 'hooks/useMartyContext';
import AccountIcon from 'components/hf/zappos/AccountIcon';
import { useAccountMenu } from 'hooks/useAccountMenu';
import type { AccountMenuItem } from 'types/navigation';
import useMyAccountRedesignEnabled from 'hooks/useMyAccountRedesignEnabled';

const contentClasses = `
  relative
  right-0
  isolate
  z-50
  overflow-hidden
  rounded-sm
  bg-gray-0
  py-4
  shadow-[2px_8px_16px_rgba(0,0,0,0.12)]
`;

export const DropdownItem = ({ dataTestId, Icon, link, text, onClick }: AccountMenuItem) => {
  const { testId } = useMartyContext();

  return (
    <Item className="!outline-none" asChild key={text}>
      <a
        aria-disabled
        aria-label={text}
        className="flex items-center justify-between px-5 py-1 transition-colors data-[highlighted]:bg-gray-100"
        data-test-id={testId(dataTestId)}
        href={link}
        onClick={onClick}
      >
        <span>{text}</span>
        {Icon && <Icon size={24} />}
      </a>
    </Item>
  );
};

interface AccountDropdownProps {
  container: MutableRefObject<HTMLDivElement>;
}

const AccountDropdown = ({ container }: AccountDropdownProps) => {
  const { testId } = useMartyContext();
  const accountMenu = useAccountMenu();
  const accountDropdownMenu = useMemo(
    () => accountMenu.map(menuItem => <DropdownItem key={menuItem.dataTestId} {...menuItem} />),
    [accountMenu.length]
  );
  useMyAccountRedesignEnabled();
  return (
    <Root>
      <Trigger asChild>
        <AccountIcon />
      </Trigger>
      <Portal container={container.current}>
        <Content className={contentClasses} avoidCollisions={false} side="bottom" align="end">
          <Group className="body-s-plus flex flex-col gap-y-2" data-test-id={testId('headerMyAccountDropdown')}>
            {accountDropdownMenu}
          </Group>
        </Content>
      </Portal>
    </Root>
  );
};

export default AccountDropdown;
